import type React from "react";
import { useState } from "react";
import {
  Box,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Paper,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

interface DynamicListFieldProps {
  items: string[];
  onItemsChange: (items: string[]) => void;
  placeholder: string;
}

const DynamicListField: React.FC<DynamicListFieldProps> = ({
  items,
  onItemsChange,
  placeholder,
}) => {
  const [newItem, setNewItem] = useState("");

  const handleAddItem = () => {
    if (newItem.trim()) {
      onItemsChange([...items, newItem.trim()]);
      setNewItem("");
    }
  };

  const handleRemoveItem = (index: number) => {
    onItemsChange(items.filter((_, i) => i !== index));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleAddItem();
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder={placeholder}
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          onKeyPress={handleKeyPress}
          variant="outlined"
        />
        <Button
          variant="contained"
          onClick={handleAddItem}
          startIcon={<AddIcon />}
          sx={{ minWidth: "auto" }}
        >
          Add
        </Button>
      </Box>

      {items.length > 0 ? (
        <List sx={{ backgroundColor: "background.paper", borderRadius: 1 }}>
          {items.map((item, index) => (
            <ListItem
              key={index}
              secondaryAction={
                <IconButton edge="end" onClick={() => handleRemoveItem(index)}>
                  <DeleteIcon />
                </IconButton>
              }
              sx={{
                borderBottom:
                  index < items.length - 1 ? "1px solid #e0e0e0" : "none",
                "&:last-child": { borderBottom: "none" },
              }}
            >
              <ListItemText primary={item} />
            </ListItem>
          ))}
        </List>
      ) : (
        <Paper
          sx={{
            p: 2,
            textAlign: "center",
            backgroundColor: "background.paper",
            color: "textSecondary",
          }}
        >
          No items added yet
        </Paper>
      )}
    </Box>
  );
};

export default DynamicListField;
